// §11.14 (D-335) — Alarms & Stopwatch, as a pure model.
//
// It lives in `shared` because **both halves need it**: the kernel service that keeps the schedule
// running while the app's window is shut, and the app that draws it. §6.5 forbids `apps/**` from
// importing `kernel/**`, so a model either sits here or gets written twice.
//
// **An alarm is a deadline, not a time of day.** That is the same choice `apps/clock/timers.ts`
// makes and for the same reason, one step further: matching "is it 07:30 right now" against a
// one-second tick works only while somebody is watching. Storing the *instant* the alarm should
// next ring means a tab that was closed over breakfast comes back knowing the alarm was missed,
// and a laptop that slept through it rings the moment it wakes rather than silently skipping a
// day. Everything below is arithmetic on that instant.
//
// Every function takes `now` rather than reading a clock, so the whole model is testable without
// waiting and without a fake global.

/** 0 = Sunday, matching `Date.getDay()`, which is what every conversion here goes through. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const WEEKDAYS: readonly Weekday[] = [0, 1, 2, 3, 4, 5, 6];

export interface Alarm {
  readonly id: string;
  readonly label: string;
  /** Local time. `hour` is 0–23 — a 12-hour display is the view's business, not the model's. */
  readonly hour: number;
  readonly minute: number;
  /**
   * Which days it repeats on. **Empty means once**, at the next occurrence — which is what somebody
   * setting an alarm for a nap or a meeting means, and it is the case a "repeat: never" flag would
   * have expressed twice.
   */
  readonly days: readonly Weekday[];
  readonly enabled: boolean;
  /**
   * When it next rings. `null` for a spent one-shot or a disabled alarm.
   *
   * Recomputed on every change and after every ring, so it is always ahead of `now` except in the
   * moment it is due — which is precisely the state `dueAlarms` looks for.
   */
  readonly nextAt: number | null;
  /** Set while snoozed. Overrides `nextAt`, and is cleared by the ring it causes. */
  readonly snoozedUntil: number | null;
  /** The last instant it actually rang. Shown to the user; never part of the schedule. */
  readonly lastRangAt: number | null;
}

/**
 * Nine minutes.
 *
 * The number is not arbitrary and it is not ours: mechanical alarm clocks put the snooze gear one
 * tooth short of ten minutes, and every clock since has kept it. Somebody who has used an alarm
 * before expects nine, and a rounder number would feel wrong without their being able to say why.
 */
export const SNOOZE_MS = 9 * 60_000;

/** How far ahead `computeNextAt` will search. Eight days covers any weekday set that is non-empty. */
const SEARCH_DAYS = 8;

export const isValidHour = (value: number): boolean =>
  Number.isInteger(value) && value >= 0 && value <= 23;
export const isValidMinute = (value: number): boolean =>
  Number.isInteger(value) && value >= 0 && value <= 59;

/**
 * The first instant **strictly after** `from` at which this alarm's time of day comes round on one
 * of its days, or `null` if it never does.
 *
 * Built by walking whole local days and setting the time on each, rather than by adding
 * milliseconds: that is what makes it correct across a daylight-saving boundary. On the day the
 * clocks go forward, 02:30 does not exist and `new Date(y, m, d, 2, 30)` normalises to 03:30 —
 * which is the answer a person expects from an alarm, and is not the answer arithmetic on
 * `86_400_000` would have given for the rest of the week either.
 */
export function computeNextAt(
  alarm: Pick<Alarm, 'hour' | 'minute' | 'days'>,
  from: number,
): number | null {
  if (!isValidHour(alarm.hour) || !isValidMinute(alarm.minute)) return null;
  const allowed = new Set(alarm.days);
  const start = new Date(from);

  for (let offset = 0; offset < SEARCH_DAYS; offset += 1) {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset);
    if (allowed.size > 0 && !allowed.has(day.getDay() as Weekday)) continue;
    const at = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      alarm.hour,
      alarm.minute,
      0,
      0,
    ).getTime();
    // Strictly after: an alarm re-armed in the same minute it rang must go to the next occurrence
    // rather than ringing again immediately.
    if (at > from) return at;
  }
  return null;
}

/** When this alarm will actually sound — a snooze wins over the schedule while it lasts. */
export function ringAt(alarm: Alarm): number | null {
  if (!alarm.enabled) return null;
  return alarm.snoozedUntil ?? alarm.nextAt;
}

/**
 * Every alarm that should have rung by `now` and has not.
 *
 * "By now", not "at now": an alarm whose instant passed while the tab was closed is still due, and
 * ringing late is the only useful thing to do with it. The alternative — skipping anything more
 * than a few seconds stale — is an alarm that silently does nothing on the one morning it mattered.
 */
export function dueAlarms(alarms: readonly Alarm[], now: number): Alarm[] {
  return alarms.filter((alarm) => {
    const at = ringAt(alarm);
    return at !== null && at <= now;
  });
}

/** How late a ring is. The app says so, because a stale alarm is a different message. */
export const lateBy = (alarm: Alarm, now: number): number => {
  const at = ringAt(alarm);
  return at === null ? 0 : Math.max(0, now - at);
};

/**
 * Advance an alarm past the ring it has just done.
 *
 * A repeating alarm re-arms for its next day. **A one-shot switches itself off** rather than
 * disappearing: the row stays where it was with its toggle off, so re-arming it is one click and
 * nobody has to retype a time they already entered.
 */
export function afterRing(alarm: Alarm, now: number): Alarm {
  const repeating = alarm.days.length > 0;
  return {
    ...alarm,
    snoozedUntil: null,
    lastRangAt: now,
    enabled: repeating,
    nextAt: repeating ? computeNextAt(alarm, now) : null,
  };
}

/** Snooze: nine more minutes, from now rather than from when it was due. */
export function snooze(alarm: Alarm, now: number, by: number = SNOOZE_MS): Alarm {
  return { ...alarm, enabled: true, snoozedUntil: now + by, lastRangAt: now };
}

/** Turn an alarm on or off, re-arming it from `now` when it comes back on. */
export function setEnabled(alarm: Alarm, enabled: boolean, now: number): Alarm {
  if (!enabled) return { ...alarm, enabled: false, snoozedUntil: null, nextAt: null };
  return { ...alarm, enabled: true, snoozedUntil: null, nextAt: computeNextAt(alarm, now) };
}

/** A new alarm, armed. `id` is the caller's, so the model needs no entropy of its own. */
export function createAlarm(
  id: string,
  fields: { label: string; hour: number; minute: number; days?: readonly Weekday[] },
  now: number,
): Alarm {
  const days = [...new Set(fields.days ?? [])].sort() as Weekday[];
  const base = { hour: fields.hour, minute: fields.minute, days };
  return {
    id,
    label: fields.label,
    ...base,
    enabled: true,
    nextAt: computeNextAt(base, now),
    snoozedUntil: null,
    lastRangAt: null,
  };
}

// ── The stopwatch ────────────────────────────────────────────────────────────────────────────

export interface Stopwatch {
  readonly running: boolean;
  /** When the current run began. `null` while stopped. */
  readonly startedAt: number | null;
  /** Milliseconds banked by previous runs — everything before the current `startedAt`. */
  readonly elapsedMs: number;
  /** Total elapsed at each lap, oldest first. */
  readonly laps: readonly number[];
}

export const IDLE_STOPWATCH: Stopwatch = {
  running: false,
  startedAt: null,
  elapsedMs: 0,
  laps: [],
};

/**
 * Total elapsed time.
 *
 * Banked-plus-live rather than a counter something increments: a stopwatch that ticked would lose
 * exactly the time the tab spent in the background, which is the one thing it exists to measure.
 */
export function elapsedOf(watch: Stopwatch, now: number): number {
  if (!watch.running || watch.startedAt === null) return watch.elapsedMs;
  return watch.elapsedMs + Math.max(0, now - watch.startedAt);
}

export function startWatch(watch: Stopwatch, now: number): Stopwatch {
  return watch.running ? watch : { ...watch, running: true, startedAt: now };
}

export function stopWatch(watch: Stopwatch, now: number): Stopwatch {
  if (!watch.running) return watch;
  return { ...watch, running: false, startedAt: null, elapsedMs: elapsedOf(watch, now) };
}

/** A lap records the total, not the split — the split is a subtraction the view can do. */
export function lapWatch(watch: Stopwatch, now: number): Stopwatch {
  if (!watch.running) return watch;
  return { ...watch, laps: [...watch.laps, elapsedOf(watch, now)] };
}

export const resetWatch = (): Stopwatch => IDLE_STOPWATCH;

/** Each lap's own duration: the first from zero, the rest from the one before. */
export function lapSplits(laps: readonly number[]): number[] {
  return laps.map((total, index) => total - (laps[index - 1] ?? 0));
}

// ── Formatting and parsing ───────────────────────────────────────────────────────────────────

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

/**
 * `mm:ss.hh`, and `h:mm:ss.hh` past the hour.
 *
 * Hundredths rather than milliseconds: three digits changing sixty times a second is noise nobody
 * can read, and two is the resolution every stopwatch has ever shown.
 */
export function formatStopwatch(ms: number): string {
  const total = Math.max(0, Math.floor(ms));
  const hundredths = Math.floor((total % 1000) / 10);
  const seconds = Math.floor(total / 1000) % 60;
  const minutes = Math.floor(total / 60_000) % 60;
  const hours = Math.floor(total / 3_600_000);
  const head = hours > 0 ? `${hours}:${pad(minutes)}` : String(minutes);
  return `${head}:${pad(seconds)}.${pad(hundredths)}`;
}

/** `07:30` — the alarm's own time, in the 24-hour form the model stores. */
export const formatTimeOfDay = (hour: number, minute: number): string =>
  `${pad(hour)}:${pad(minute)}`;

/**
 * What somebody types into a time box: `7:30`, `07:30`, `19:05`, `7:30am`, `7.30 PM`, or `730`.
 *
 * Deliberately generous about the separator and the case, and deliberately strict about the
 * result: an hour of 25 is not a time, and returning `null` is how the form knows to keep the
 * Add button disabled rather than quietly storing something else.
 */
export function parseTimeOfDay(input: string): { hour: number; minute: number } | null {
  const text = input.trim().toLowerCase().replace(/\s+/g, '');
  if (text.length === 0) return null;

  const match = /^(\d{1,2})[:.]?(\d{2})?(am|pm)?$/.exec(text);
  if (!match) return null;
  const [, rawHour = '', rawMinute, suffix] = match;

  // `730` is 7:30 and `7` is 7:00 — a bare three- or four-digit number is a time somebody typed
  // without reaching for the colon, and refusing it would be pedantry.
  let hour = Number(rawHour);
  const minute = rawMinute === undefined ? 0 : Number(rawMinute);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (!isValidMinute(minute)) return null;

  if (suffix !== undefined) {
    if (hour < 1 || hour > 12) return null;
    hour = suffix === 'pm' ? (hour % 12) + 12 : hour % 12;
  }
  if (!isValidHour(hour)) return null;
  return { hour, minute };
}

/** `Every day` · `Weekdays` · `Mon, Thu` · `Once` — the repeat, in words. */
export const DAY_NAMES: readonly string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function describeDays(days: readonly Weekday[]): string {
  if (days.length === 0) return 'Once';
  const set = new Set(days);
  if (set.size === 7) return 'Every day';
  if (set.size === 5 && [1, 2, 3, 4, 5].every((day) => set.has(day as Weekday))) return 'Weekdays';
  if (set.size === 2 && set.has(0) && set.has(6)) return 'Weekends';
  return [...set]
    .sort()
    .map((day) => DAY_NAMES[day] ?? '')
    .join(', ');
}
